import * as React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import cx from 'clsx';
import { Button } from '@repo/design-system-kit/components/button/button';
import { ExternalLink } from '@repo/design-system-kit/components/external-link/external-link';
import { TextField } from '@repo/design-system-kit/components/text-field/text-field';
import { IconCopyOutlined, IconExternalOutlined } from '@repo/monochrome-icons';
import { useTrackRender } from '@repo/shared-kit/hooks/use-track-render';
import { os } from '../../../utils/device';
import { appURL } from '../lib/redirects';
import styles from './landing-mobile.module.css';

export function LandingMobile({
  token,
  hasGermanResidencePermit,
  onContinue,
  trackingPrefix,
}: {
  token: string;
  hasGermanResidencePermit: boolean;
  onContinue: (link: string) => void;
  trackingPrefix: string;
}): React.JSX.Element {
  const intl = useIntl();
  const idnowWebsiteHref = intl.formatMessage({
    id: 'identity-verification-kit.links.idnowWebsite',
  });
  const idnowTermsHref = intl.formatMessage({ id: 'identity-verification-kit.links.idnowTerms' });
  const idnowPrivacyHref = intl.formatMessage({
    id: 'identity-verification-kit.links.idnowPrivacy',
  });

  useTrackRender({ eventName: `${trackingPrefix}_idnow_mobile_screen_loaded` });

  const link = appURL(token);

  return (
    <div className={styles.container}>
      <h1 className="heading-section mb-16">
        <FormattedMessage
          defaultMessage="Verify your identity"
          id="identity-verification-kit.qonto-idnow.intro.title"
        />
      </h1>
      <p className="body-standard mb-32">
        {hasGermanResidencePermit ? (
          <FormattedMessage
            id="identity-verification-kit.qonto-idnow.computer.intro.residence-permit.subtitle"
            values={{
              idnowLink: (chunks: React.ReactNode) => (
                <a
                  className="body-standard-link"
                  href={idnowWebsiteHref}
                  rel="noopener"
                  target="_blank"
                >
                  {chunks}
                </a>
              ),
            }}
          />
        ) : (
          <FormattedMessage
            id="identity-verification-kit.qonto-idnow.computer.intro.subtitle"
            values={{
              idnowLink: (chunks: React.ReactNode) => (
                <a
                  className="body-standard-link"
                  href={idnowWebsiteHref}
                  rel="noopener"
                  target="_blank"
                >
                  {chunks}
                </a>
              ),
              idnowTermsLink: (chunks: React.ReactNode) => (
                <a
                  className="body-standard-link"
                  href={idnowTermsHref}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  {chunks}
                </a>
              ),
              idnowPrivacyLink: (chunks: React.ReactNode) => (
                <a
                  className="body-standard-link"
                  href={idnowPrivacyHref}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  {chunks}
                </a>
              ),
            }}
          />
        )}
      </p>

      <div className="mb-32">
        <h2 className="body-prominent mb-12">
          <FormattedMessage
            defaultMessage="1. Copy the following code (Ident-ID)"
            id="identity-verification-kit.qonto-idnow.mobile.intro.list-item1"
          />
        </h2>
        <p className="body-standard mb-12">
          <FormattedMessage
            defaultMessage="You’ll need it to start your identification."
            id="identity-verification-kit.qonto-idnow.mobile.intro.list-item1-subtitle"
          />
        </p>

        <div className={styles.copy}>
          <TextField isReadOnly label="" placeholder="" value={token} />

          <Button
            onPress={() => {
              void window.navigator.clipboard.writeText(token);
            }}
            variant="secondary"
          >
            <FormattedMessage
              defaultMessage="Copy"
              id="identity-verification-kit.qonto-idnow.mobile.intro.list-item1-cta"
            />
            <IconCopyOutlined className="ml-8" />
          </Button>
        </div>
      </div>

      <div>
        <h2 className="body-prominent mb-16">
          <FormattedMessage
            defaultMessage="2. Download the IDnow Online Ident app"
            id="identity-verification-kit.qonto-idnow.mobile.intro.list-item2"
          />
        </h2>

        <ExternalLink
          display="button"
          href={link}
          onPress={() => {
            onContinue(link);
          }}
          stretch
          target="_blank"
        >
          {os?.name === 'Android' ? (
            <FormattedMessage
              defaultMessage="Download on the Play Store"
              id="identity-verification-kit.qonto-idnow.mobile.intro.cta-android"
            />
          ) : (
            <FormattedMessage
              defaultMessage="Download on the App Store"
              id="identity-verification-kit.qonto-idnow.mobile.intro.cta"
            />
          )}
          <IconExternalOutlined className={cx('ml-8', styles.linkExternal)} />
        </ExternalLink>
      </div>
    </div>
  );
}
